import { create } from 'zustand'

export interface User {
  email: string
  name: string
  given_name?: string
  family_name?: string
  roles: string[]
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  setUser: (user: User | null) => void
  setAuthenticated: (authenticated: boolean) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  logout: () => void
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
}

export const useAuthStore = create<AuthState & AuthActions>((set) => ({
  ...initialState,

  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
      isLoading: false,
      error: null,
    }),

  setAuthenticated: (authenticated) =>
    set({ isAuthenticated: authenticated }),

  setLoading: (loading) =>
    set({ isLoading: loading }),

  setError: (error) =>
    set({ error, isLoading: false }),

  logout: () =>
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    }),
}))

// Helper to extract user from OIDC profile
export function extractUser(profile: Record<string, unknown>): User {
  const realmAccess = profile.realm_access as { roles?: string[] } | undefined
  const roles = realmAccess?.roles || []

  return {
    email: profile.email as string,
    name: profile.name as string || profile.preferred_username as string || '',
    given_name: profile.given_name as string | undefined,
    family_name: profile.family_name as string | undefined,
    roles,
  }
}

// Permission checking helpers
export function hasRole(user: User | null, role: string): boolean {
  return user?.roles?.includes(role) ?? false
}

export function hasAnyRole(user: User | null, roles: string[]): boolean {
  return roles.some((role) => hasRole(user, role))
}

export function isAdmin(user: User | null): boolean {
  return hasAnyRole(user, ['admin', 'sys_admin', 'sys_op'])
}
